import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { TestimonialsProps } from 'lib/components/Testimonials/Testimonials'

import { StructuredData } from '@/utils/brand'

import { HomePageContent } from './shipsticks.page.content'

const structuredData: StructuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://www.shipskis.com/#organization',
      name: 'ShipSkis',
      url: 'https://www.shipskis.com',
      sameAs: [
        'https://www.facebook.com/ShipSkis',
        'https://www.linkedin.com/company/ship-skis/',
        'https://www.instagram.com/shipskis/',
        'https://www.youtube.com/channel/UCAEktwfaf5Wk4Wn4yzk8ZVQ',
        'https://www.tiktok.com/@shipskis',
        'https://x.com/ship_skis',
      ],
      logo: {
        '@type': 'ImageObject',
        '@id': 'https://www.shipskis.com/#logo',
        url: 'https://www.shipskis.com/_next/static/media/ship-skis-dark.4120a304.svg',
        caption: 'ShipSkis',
      },
      image: {
        '@id': 'https://www.shipskis.com/#logo',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '11400 N. Jog Road, Suite 300',
        addressLocality: 'Palm Beach Gardens',
        addressCountry: 'US',
        addressRegion: 'Florida',
        postalCode: '33418',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-866-761-7547',
        email: 'customersupport@shipskis.com',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://www.shipskis.com/#website',
      url: 'https://www.shipskis.com',
      name: 'ShipSkis',
      description: 'Ski and snowboard shipping to resorts and mountains around the world.',
      publisher: {
        '@id': 'https://www.shipskis.com/#organization',
      },
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.shipskis.com/blog/?s={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'ImageObject',
      '@id': 'https://www.shipskis.com/#primaryimage',
      url: 'https://www.shipskis.com/_next/static/media/ship-skis-dark.4120a304.svg',
    },
    {
      '@type': 'WebPage',
      '@id': 'https://www.shipskis.com/#webpage',
      url: 'https://www.shipskis.com',
      inLanguage: 'en-US',
      name: 'ShipSkis: Safe, convenient, and hassle-free travel. Guaranteed.',
      isPartOf: {
        '@id': 'https://www.shipskis.com/#webpage',
      },
      about: {
        '@id': 'https://www.shipskis.com/#organization',
      },
      primaryImageOfPage: {
        '@id': 'https://www.shipskis.com/#primaryimage',
      },
      description:
        'Travel light and skip the baggage claim. Ship your snow gear to your destination ' +
        'with safe, on-time service.',
    },
    {
      '@type': 'Product',
      url: 'https://www.shipskis.com/',
      name: 'Ski & Snowboard Shipping by ShipSkis',
      description:
        'Door-to-door shipping for skis, snowboards, and winter gear. Trusted by thousands ' +
        'of skiers and riders globally.',
      image: 'https://www.shipskis.com/_next/static/media/ship-skis-dark.4120a304.svg',
      brand: {
        '@type': 'Organization',
        '@id': 'https://www.shipskis.com/#organization',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        reviewCount: '2504',
        bestRating: '5',
      },
    },
  ],
} as const

const blogMeta = {
  buttonText: 'Continue reading',
  subheader:
    'Explore trending ski destinations, get insider travel tips, ' +
    "and see what's new in winter sport and lifestyle.",
}

const heroMeta = {
  header: <>Great ski trips start here.</>,
  subheader: 'Ship your skis, snowboards and luggage ahead worldwide.',
  image: '/images/shipskis/homepage-hero.jpg',
  stepsHeader: (
    <>
      Ship your items in just three <b>easy steps.</b>
    </>
  ),
  steps: [
    {
      header: 'Book',
      subheader:
        'Easily book online, through the app, or over the phone - whatever works best for you.',
    },
    {
      header: 'Pack',
      subheader: `
          Pack your bags as you would for airline travel and attach your shipping label.
          We'll take it from there.
        `,
    },
    {
      header: 'Play',
      subheader: `
          Step off the plane and head straight to your destination. Your gear is ready and waiting,
          and shipping your luggage home is just as easy.
        `,
    },
  ],
}

const fiftyFiftiesMeta: FiftyFiftyProps[] = [
  {
    header: (
      <>
        Less heavy lifting, <strong>more peak living.</strong>
      </>
    ),
    body: (
      <>
        Skip the hassle of hauling bulky skis and gear through airports or waiting at baggage claim.
        We make it simple to <strong>ship skis</strong> safely to your destination — stress-free and
        designed for skiers and riders like you.
      </>
    ),
    image: {
      src: '/images/shipskis/homepage-1.jpg',
    },
    button: {
      text: 'Get started',
      action: 'booking-widget-redirect',
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        It is <strong>easy</strong> to ship.
      </>
    ),
    body: `
      Just pack your skis, board, or winter gear as you would for a flight 
      and we'll handle the rest - from picking up at your doorstep, to delivery 
      at your mountain lodge. Or, you can drop them off at a trusted local 
      carrier before you head out.
    `,
    image: {
      src: '/images/shipskis/homepage-2.jpg',
    },
    button: {
      text: 'How it works',
      link: {
        href: '/how-it-works',
      },
    },
    layout: 'image-left',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        Winter travel, <strong>transformed.</strong>
      </>
    ),
    body: `
      Forget airport delays, oversized baggage fees, and settling for rentals. 
      With ShipSkis, you can ship your skis ahead and travel light, so you 
      arrive at the slopes relaxed and ready for your first run.
    `,
    image: {
      src: '/images/shipskis/homepage-3.jpg',
    },
    button: {
      text: 'Try it out',
      action: 'booking-widget-redirect',
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        We treat your skis <strong>like our own.</strong>
      </>
    ),
    body: `
      Every shipment includes real-time tracking, complimentary insurance, 
      and support from people who actually get ski travel. That's a service 
      you won’t find from the airlines.
    `,
    image: {
      src: '/images/shipskis/homepage-4.jpg',
    },
    button: {
      text: 'What we offer',
      link: {
        href: '/guarantee',
      },
    },
    layout: 'image-left',
    backgroundColor: 'grey',
  },
  {
    header: (
      <>
        Your winter, <strong>your way.</strong>
      </>
    ),
    body: `
      We ship worldwide and work around your schedule. Have your skis waiting 
      at the resort, sent home after your final run, or delivered to every 
      stop along the way. Wherever you go, travel confidently 
      knowing we've got you covered.
    `,
    image: {
      src: '/images/shipskis/homepage-5.jpg',
    },
    button: {
      text: 'Discover more',
      link: {
        href: 'https://www.shipskis.com/blog/travel/',
      },
    },
    layout: 'image-right',
    backgroundColor: 'grey',
  },
]

const testimonialsMeta: TestimonialsProps = {
  title: (
    <>
      What our customers <strong>have&nbsp;to&nbsp;say</strong>
    </>
  ),
  testimonials: [
    {
      stars: 5,
      quote: `
        ShipSkis was a game changer for our family of 5. The skis arrived faster than
        expected and not having to lug 90lbs of gear through the airport was such a relief!
      `,
      attribution: {
        icon: 'resort',
        name: 'Michelle M',
      },
    },
    {
      stars: 5,
      quote: `
        Took me less than 5 minutes to drop off my skis and they arrived exactly as expected.
        I will not fly with my skis and boots ever again.
      `,
      attribution: {
        icon: 'resort',
        name: 'Tim W',
      },
    },
    {
      stars: 4,
      quote: `
        We've used ShipSkis for years. It's a perfect trip when they handle our skis,
        boots & luggage.
      `,
      attribution: {
        icon: 'resort',
        name: 'Sandra W',
      },
    },
  ],
}

const content: HomePageContent = {
  blogMeta,
  heroMeta,
  fiftyFiftiesMeta,
  testimonialsMeta,
  structuredData,
}

export default content
